import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { applyFontFromClient } from '../utils/clientStyles';
import { normalizeClientMetaBranding, applyClientFavicon } from '../utils/clientBranding';
import { PageShimmer } from '../components/Shimmer/PageShimmer';

const ClientContext = createContext();

/**
 * Hook to access client metadata throughout the application
 * @returns {Object} Client metadata including companyName, features, contact, social, branding
 */
export const useClient = () => {
    const context = useContext(ClientContext);
    if (!context) {
        throw new Error('useClient must be used within ClientProvider');
    }
    return context;
};

const STORAGE_KEY = 'client';
const FEATURES_KEY = 'features';
const CACHE_VERSION_COOKIE = 'cv';

/**
 * Get cached client data from localStorage if cache is valid
 */
const getCachedClient = () => {
    try {
        const cached = localStorage.getItem(STORAGE_KEY);
        const cv = Cookies.get(CACHE_VERSION_COOKIE);
        if (cached && cv) return normalizeClientMetaBranding(JSON.parse(cached));
    } catch (err) {
        console.warn('Failed to read cached client data:', err);
    }

    return null;
};

/**
 * Get cached features from localStorage if cache is valid
 */
const getCachedFeatures = () => {
    try {
        const cached = localStorage.getItem(FEATURES_KEY);
        const cv = Cookies.get(CACHE_VERSION_COOKIE);
        if (cached && cv) return JSON.parse(cached);
    } catch (err) {
        console.warn('Failed to read cached features:', err);
    }

    return null;
};

/**
 * Save client data to localStorage and set cache cookie
 */
const setCachedClient = (data) => {
    try {
        const normalized = normalizeClientMetaBranding(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
        Cookies.set(CACHE_VERSION_COOKIE, '1', { expires: 1 / 24 });
        return normalized;
    } catch (err) {
        console.warn('Failed to cache client data:', err);
        return data;
    }
};

const setCachedFeatures = (data) => {
    try {
        localStorage.setItem(FEATURES_KEY, JSON.stringify(data));
        Cookies.set(CACHE_VERSION_COOKIE, '1', { expires: 1 / 24 });
    } catch (err) {
        console.warn('Failed to cache features:', err);
    }
};

/**
 * Provider component that fetches and provides client metadata + features.
 * Gate: nothing visible until BOTH client AND features are set in localStorage.
 */
export const ClientProvider = ({ children }) => {
    const cachedClient = getCachedClient();
    const cachedFeatures = getCachedFeatures();
    const bothCached = !!(cachedClient && cachedFeatures);

    const [client, setClient] = useState(cachedClient);
    const [isLoading, setIsLoading] = useState(!bothCached);
    const [error, setError] = useState(null);

    // Rewrite stale localStorage entries (e.g. raw S3 logo/favicon) on first load.
    useEffect(() => {
        const cached = getCachedClient();
        if (!cached) return;
        try {
            const normalized = normalizeClientMetaBranding(cached);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
            setClient((prev) => (prev === cached ? normalized : prev));
        } catch (err) {
            console.warn('Failed to normalize cached client data:', err);
        }
    }, []);

    useEffect(() => {
        if (client) applyFontFromClient(client);
    }, [client]);

    // Replace favicon by id when client has favicon (use id="app-favicon", default /static/favicon.ico)
    useEffect(() => {
        applyClientFavicon(client?.favicon);
    }, [client]);

    useEffect(() => {
        if (bothCached) {
            setIsLoading(false);
            return;
        }

        const fetchBoth = async () => {
            try {
                const [metaRes, featuresRes] = await Promise.all([
                    fetch('/api/client/cookie/meta?app=job-portal'),
                    fetch('/api/client/cookie/features?app=job-portal')
                ]);
                const metaData = metaRes.ok ? await metaRes.json() : null;
                const featuresData = featuresRes.ok ? await featuresRes.json() : null;

                // API returns exact `app` query param as key (e.g. "job-portal": true). Features in .data.
                const features = featuresData?.success && (featuresData?.features || featuresData?.data)
                    ? (featuresData.features ?? featuresData.data)
                    : {};
                const mergedClient = metaData?.success && metaData?.data
                    ? normalizeClientMetaBranding({ ...metaData.data, features })
                    : { features };
                if (mergedClient && (metaData?.success || Object.keys(mergedClient).length > 0)) {
                    setClient(mergedClient);
                    if (metaData?.success && metaData?.data) {
                        setCachedClient(mergedClient);
                        Cookies.set(CACHE_VERSION_COOKIE, '1', { expires: 1 / 24 });
                    }
                }
                if (Object.keys(features).length > 0) {
                    setCachedFeatures(features);
                    Cookies.set(CACHE_VERSION_COOKIE, '1', { expires: 1 / 24 });
                }
                // Always allow app to render after fetch (even on partial/failed response)
                // so whitelabel PartnerPage can load and make its own API calls
                setIsLoading(false);
            } catch (err) {
                console.error('Failed to fetch client/features:', err);
                setError(err.message);
                setIsLoading(false);
                setClient((c) => c || { features: {} });
                setCachedFeatures({});
            }
        };

        fetchBoth();
    }, [bothCached]);

    const value = {
        ...(client || {}),
        isLoading,
        error,
        hasFeature: (name) => client?.features?.[name] ?? false,
    };

    if (isLoading) {
        return (
            <ClientContext.Provider value={value}>
                <div style={{ fontFamily: 'var(--font-primary)' }}>
                    <PageShimmer variant="table" />
                </div>
            </ClientContext.Provider>
        );
    }

    return (
        <ClientContext.Provider value={value}>
            {children}
        </ClientContext.Provider>
    );
};

export default ClientContext;
